import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { goalService, Todo, TimeEntry } from '../services/goalService';
import { useTranslation } from '../i18n';
import { useToast } from './ToastContext';
import { getErrorMessage } from '../types/error';
import { elapsedSecondsSince, isLongRunning } from '../utils/timer';
import { useTimerDocumentTitle } from '../hooks/useTimerDocumentTitle';
import DurationCorrectionModal from '../components/DurationCorrectionModal/DurationCorrectionModal';

const REFRESH_INTERVAL_MS = 60_000;

export interface RunningTimer {
  todoId: string;
  title: string;
  startedAt: string;
}

interface CorrectionTarget {
  todoId: string;
  entryId: string;
  title?: string;
  recordedSeconds: number;
  isLongRunningPrompt: boolean;
  onSaved?: () => void;
}

interface TimerContextValue {
  running: RunningTimer[];
  refresh: () => Promise<void>;
  /** Stops the timer; for forgotten timers (8h+) asks the user to correct the recorded duration. */
  stopTimer: (todoId: string, options?: { startedAt?: string | null; onCorrected?: () => void; note?: string }) => Promise<Todo | null>;
  correctEntry: (todoId: string, entry: TimeEntry, options?: { title?: string; onSaved?: () => void }) => void;
}

const TimerContext = createContext<TimerContextValue | null>(null);

export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [running, setRunning] = useState<RunningTimer[]>([]);
  const [correction, setCorrection] = useState<CorrectionTarget | null>(null);
  const runningRef = useRef(running);
  runningRef.current = running;
  useTimerDocumentTitle(running);

  const refresh = useCallback(async () => {
    try {
      const { todos } = await goalService.getTimelyTodos();
      setRunning(
        todos
          .filter((todo) => todo.isStarted && !todo.completed)
          .map((todo) => ({
            todoId: todo.id,
            title: todo.title,
            startedAt: todo.activeTimeEntry?.startedAt || todo.startedAt || '',
          }))
          .filter((timer) => timer.startedAt)
      );
    } catch {
      // The indicator is best-effort; pages surface their own load errors.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  const stopTimer = useCallback<TimerContextValue['stopTimer']>(async (todoId, options) => {
    const timer = runningRef.current.find((r) => r.todoId === todoId);
    const elapsed = elapsedSecondsSince(options?.startedAt || timer?.startedAt);
    try {
      const updated = await goalService.stopTodo(todoId, options?.note?.trim() || undefined);
      setRunning((prev) => prev.filter((r) => r.todoId !== todoId));
      if (isLongRunning(elapsed)) {
        const { timeEntries } = await goalService.getTimeEntries(todoId);
        const latest = timeEntries.find((entry) => entry.stoppedAt);
        if (latest) {
          setCorrection({
            todoId,
            entryId: latest.id,
            title: timer?.title || updated.title,
            recordedSeconds: latest.durationSeconds,
            isLongRunningPrompt: true,
            onSaved: options?.onCorrected,
          });
        }
      }
      return updated;
    } catch (err) {
      toast.error(getErrorMessage(err, t('errors.stopTodo')), {
        actions: [{ label: t('common.retryAction'), onClick: () => { void stopTimer(todoId, options); } }],
      });
      return null;
    }
  }, [t, toast]);

  const correctEntry = useCallback<TimerContextValue['correctEntry']>((todoId, entry, options) => {
    setCorrection({
      todoId,
      entryId: entry.id,
      title: options?.title,
      recordedSeconds: entry.durationSeconds,
      isLongRunningPrompt: false,
      onSaved: options?.onSaved,
    });
  }, []);

  const handleSaveCorrection = useCallback(async (durationSeconds: number) => {
    if (!correction) return;
    try {
      await goalService.updateTimeEntry(correction.todoId, correction.entryId, { durationSeconds });
      toast.success(t('timer.durationSaved'));
      correction.onSaved?.();
      setCorrection(null);
    } catch (err) {
      toast.error(getErrorMessage(err, t('errors.saveDuration')));
    }
  }, [correction, t, toast]);

  const value = useMemo(() => ({ running, refresh, stopTimer, correctEntry }), [running, refresh, stopTimer, correctEntry]);

  return (
    <TimerContext.Provider value={value}>
      {children}
      <DurationCorrectionModal
        isOpen={!!correction}
        todoTitle={correction?.title}
        recordedSeconds={correction?.recordedSeconds ?? 0}
        isLongRunningPrompt={correction?.isLongRunningPrompt}
        onSave={handleSaveCorrection}
        onClose={() => setCorrection(null)}
      />
    </TimerContext.Provider>
  );
};

/** How many timers run now; 0 outside a TimerProvider (the nav's green dot, T-180). */
export const useRunningTimerCount = (): number => useContext(TimerContext)?.running.length ?? 0;

export const useTimers = (): TimerContextValue => {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimers must be used within TimerProvider');
  return ctx;
};
